import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  Brain, Target, TrendingUp, DollarSign, AlertCircle, Loader2, 
  CheckCircle, XCircle, Edit3, Send, RefreshCw, Shield, 
  ArrowRight, Lightbulb, AlertTriangle, Info, ChevronDown, ChevronUp,
  MessageSquare, Clipboard, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AIControlledDraft Component
 * Implements human-in-the-loop AI drafting with qualification-first approach
 */
export function AIControlledDraft({ 
  inquiryId, 
  productId, 
  inquiryMessage, 
  conversationHistory = [],
  isFirstResponse = true,
  onDraftApproved,
  onClose 
}) {
  const [draft, setDraft] = useState(null);
  const [editedDraft, setEditedDraft] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [regenerationCount, setRegenerationCount] = useState(0);
  
  // Draft settings
  const [tone, setTone] = useState('neutral');
  const [length, setLength] = useState('standard');
  const [includePrice, setIncludePrice] = useState(false);
  const [qualificationMode, setQualificationMode] = useState(true);
  
  // Available settings
  const [settings, setSettings] = useState(null);
  
  const toneOptions = [
    { key: 'soft', name: 'Soft / Relationship-First', description: 'Warm and accommodating' },
    { key: 'neutral', name: 'Neutral / Professional', description: 'Balanced business tone' },
    { key: 'firm', name: 'Firm / Commercial', description: 'Clear terms, protect margins' },
    { key: 'very_firm', name: 'Very Firm / Hold-Line', description: 'Strong position, minimal flexibility' }
  ];
  
  const lengthOptions = [
    { key: 'short', name: 'Short', description: '50-80 words' },
    { key: 'standard', name: 'Standard', description: '100-150 words' },
    { key: 'detailed', name: 'Detailed', description: '180-250 words' }
  ];
  
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/ai/draft-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };
  
  const generateDraft = async (isRegeneration = false) => {
    setIsLoading(true);
    
    // Increment regeneration count if regenerating
    const currentRegenCount = isRegeneration ? regenerationCount + 1 : 0;
    if (isRegeneration) {
      setRegenerationCount(currentRegenCount);
    }
    
    try {
      const response = await axios.post(`${API}/ai/controlled-draft`, {
        inquiry_id: inquiryId,
        product_id: productId,
        inquiry_message: inquiryMessage,
        conversation_history: conversationHistory,
        tone,
        length,
        include_price: includePrice,
        qualification_mode: qualificationMode,
        is_first_response: isFirstResponse,
        regeneration_count: currentRegenCount
      });
      
      setDraft(response.data);
      setEditedDraft(response.data.draft_message);
      
      if (response.data.has_warnings) {
        toast.warning('Draft contains risky language - review before sending');
      } else if (isRegeneration) {
        toast.success('New draft generated with different phrasing');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate draft');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSend = async () => {
    if (!editedDraft.trim()) {
      toast.error('Message cannot be empty');
      return;
    }
    
    setIsSending(true);
    try {
      if (onDraftApproved) {
        await onDraftApproved(editedDraft);
      }
      toast.success('Message sent successfully');
      onClose?.();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(editedDraft);
    toast.success('Copied to clipboard');
  };
  
  return (
    <div className="space-y-4">
      {/* Notice Banner */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">Human-in-the-Loop Draft</AlertTitle>
        <AlertDescription className="text-blue-700 text-sm">
          AI suggests drafts, you control the final message. Review and edit before sending.
        </AlertDescription>
      </Alert>
      
      {/* Settings Panel */}
      {!draft && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              Draft Settings
            </CardTitle>
            <CardDescription>Configure how the AI generates your draft</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Tone Selection */}
            <div className="space-y-2">
              <Label>Tone</Label>
              <div className="grid grid-cols-2 gap-2">
                {toneOptions.map((option) => (
                  <Button
                    key={option.key}
                    variant={tone === option.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTone(option.key)}
                    className="justify-start h-auto py-2 px-3"
                    data-testid={`tone-${option.key}-btn`}
                  >
                    <div className="text-left">
                      <p className="font-medium text-xs">{option.name}</p>
                      <p className="text-xs opacity-70">{option.description}</p>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Length Selection */}
            <div className="space-y-2">
              <Label>Length</Label>
              <div className="flex gap-2">
                {lengthOptions.map((option) => (
                  <Button
                    key={option.key}
                    variant={length === option.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setLength(option.key)}
                    data-testid={`length-${option.key}-btn`}
                  >
                    {option.name}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Options */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Qualification Mode</Label>
                  <p className="text-xs text-muted-foreground">
                    Prioritize getting commercial details before quoting
                  </p>
                </div>
                <Switch 
                  checked={qualificationMode} 
                  onCheckedChange={setQualificationMode}
                  data-testid="qualification-mode-switch"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Include Price</Label>
                  <p className="text-xs text-muted-foreground">
                    Only if buyer has shared key commercial details
                  </p>
                </div>
                <Switch 
                  checked={includePrice} 
                  onCheckedChange={setIncludePrice}
                  data-testid="include-price-switch"
                />
              </div>
            </div>
            
            <Button 
              onClick={() => generateDraft(false)} 
              className="w-full mt-4"
              disabled={isLoading}
              data-testid="generate-draft-btn"
            >
              {isLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Brain className="h-4 w-4 mr-2" /> Generate Controlled Draft</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Generated Draft */}
      {draft && (
        <div className="space-y-4">
          {/* Warnings */}
          {draft.has_warnings && (
            <Alert variant="destructive" className="bg-amber-50 border-amber-300 text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Risky Language Detected</AlertTitle>
              <AlertDescription>
                <ul className="mt-2 space-y-1 text-sm">
                  {draft.warnings.map((warning, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-amber-600">•</span>
                      {warning.message}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 font-medium">Review and edit flagged phrases before sending.</p>
              </AlertDescription>
            </Alert>
          )}
          
          {/* Qualification Status */}
          {draft.qualification_status && (
            <Card className="bg-slate-50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Buyer Qualification Status</span>
                  <Badge variant={draft.qualification_status.all_confirmed ? 'default' : 'secondary'}>
                    {draft.qualification_status.confirmed_count}/5 Details Confirmed
                  </Badge>
                </div>
                {draft.qualification_status.missing_variables?.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    <p className="font-medium mb-1">Missing from buyer:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {draft.qualification_status.missing_variables.map((v, idx) => (
                        <li key={idx}>{v.label}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Editable Draft */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Your Draft</CardTitle>
                <Badge variant="outline" className="text-xs">
                  {draft.settings_used?.tone_name} • {draft.settings_used?.length}
                </Badge>
              </div>
              <CardDescription>{draft.notice}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={editedDraft}
                onChange={(e) => setEditedDraft(e.target.value)}
                className="min-h-[150px] max-h-[350px] overflow-y-auto font-mono text-sm resize-y"
                placeholder="Your message..."
                data-testid="draft-textarea"
              />
              
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => generateDraft(true)}
                    disabled={isLoading}
                    data-testid="regenerate-btn"
                  >
                    <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                    Regenerate {regenerationCount > 0 && `(${regenerationCount})`}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={copyToClipboard}
                  >
                    <Clipboard className="h-4 w-4 mr-1" />
                    Copy
                  </Button>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSend}
                    disabled={isSending || !editedDraft.trim()}
                    data-testid="send-draft-btn"
                  >
                    {isSending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="h-4 w-4 mr-2" /> Send Message</>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}


/**
 * NegotiationBands Component
 * Internal strategy options for seller - NOT ready-to-send messages
 */
export function NegotiationBands({
  inquiryId,
  productId,
  conversationHistory = [],
  productPrice = 0,
  productUnit = 'unit',
  onStrategySelected,
  onClose
}) {
  const [bands, setBands] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showInputs, setShowInputs] = useState(true);
  const [expandedStrategy, setExpandedStrategy] = useState(null);
  
  // Seller inputs
  const [sellerInputs, setSellerInputs] = useState({
    floor_price: productPrice * 0.85,
    target_price: productPrice * 0.95,
    ideal_price: productPrice,
    moq: 100,
    trial_quantity_allowed: false,
    preferred_payment: 'LC',
    preferred_incoterm: 'FOB',
    shipment_urgency: 'standard',
    buyer_quality_score: 'medium',
    strategic_value: 'medium'
  });
  
  const generateBands = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/ai/negotiation-bands`, {
        inquiry_id: inquiryId,
        product_id: productId,
        conversation_history: conversationHistory,
        ...sellerInputs
      });
      
      setBands(response.data);
      setShowInputs(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate negotiation bands');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleStrategySelect = (strategy) => {
    if (onStrategySelected) {
      onStrategySelected(strategy);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Header Notice */}
      <Alert className="bg-purple-50 border-purple-200">
        <Shield className="h-4 w-4 text-purple-600" />
        <AlertTitle className="text-purple-800">Internal Negotiation Bands</AlertTitle>
        <AlertDescription className="text-purple-700 text-sm">
          These are internal strategy options for your review. DO NOT send these drafts as-is. 
          Review, edit, and customize before sending any message.
        </AlertDescription>
      </Alert>
      
      {/* Seller Inputs */}
      {showInputs && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5" />
              Your Negotiation Parameters
            </CardTitle>
            <CardDescription>
              Set your internal boundaries before generating strategy options
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Pricing Inputs */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Floor Price (Minimum)</Label>
                <Input
                  type="number"
                  value={sellerInputs.floor_price}
                  onChange={(e) => setSellerInputs({...sellerInputs, floor_price: parseFloat(e.target.value)})}
                  data-testid="floor-price-input"
                />
                <p className="text-xs text-muted-foreground">Absolute minimum you'll accept</p>
              </div>
              <div className="space-y-2">
                <Label>Target Price</Label>
                <Input
                  type="number"
                  value={sellerInputs.target_price}
                  onChange={(e) => setSellerInputs({...sellerInputs, target_price: parseFloat(e.target.value)})}
                  data-testid="target-price-input"
                />
                <p className="text-xs text-muted-foreground">Your realistic goal</p>
              </div>
              <div className="space-y-2">
                <Label>Ideal Price</Label>
                <Input
                  type="number"
                  value={sellerInputs.ideal_price}
                  onChange={(e) => setSellerInputs({...sellerInputs, ideal_price: parseFloat(e.target.value)})}
                  data-testid="ideal-price-input"
                />
                <p className="text-xs text-muted-foreground">Best case scenario</p>
              </div>
            </div>
            
            <Separator />
            
            {/* Buyer Assessment */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Buyer Quality Score</Label>
                <Select 
                  value={sellerInputs.buyer_quality_score}
                  onValueChange={(v) => setSellerInputs({...sellerInputs, buyer_quality_score: v})}
                >
                  <SelectTrigger data-testid="buyer-quality-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High - Serious buyer, clear intent</SelectItem>
                    <SelectItem value="medium">Medium - Interested, needs qualification</SelectItem>
                    <SelectItem value="low">Low - Uncertain commitment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Strategic Value</Label>
                <Select 
                  value={sellerInputs.strategic_value}
                  onValueChange={(v) => setSellerInputs({...sellerInputs, strategic_value: v})}
                >
                  <SelectTrigger data-testid="strategic-value-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High - Key account, must win</SelectItem>
                    <SelectItem value="medium">Medium - Standard opportunity</SelectItem>
                    <SelectItem value="low">Low - Nice to have</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Trial Quantity */}
            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Allow Trial Quantity Below MOQ?</Label>
                <p className="text-xs text-muted-foreground">For testing/samples</p>
              </div>
              <Switch 
                checked={sellerInputs.trial_quantity_allowed}
                onCheckedChange={(v) => setSellerInputs({...sellerInputs, trial_quantity_allowed: v})}
                data-testid="trial-allowed-switch"
              />
            </div>
            
            <Button 
              onClick={generateBands} 
              className="w-full"
              disabled={isLoading}
              data-testid="generate-bands-btn"
            >
              {isLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating Strategies...</>
              ) : (
                <><Brain className="h-4 w-4 mr-2" /> Generate Negotiation Bands</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Generated Bands */}
      {bands && (
        <div className="space-y-4">
          {/* Price Summary */}
          <Card className="bg-slate-50">
            <CardContent className="pt-4">
              <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Your Negotiation Range
              </h4>
              <div className="grid grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Floor</p>
                  <p className="font-semibold text-red-600">{bands.price_summary.seller_floor_price}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Target</p>
                  <p className="font-semibold text-amber-600">{bands.price_summary.seller_target_price}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Ideal</p>
                  <p className="font-semibold text-green-600">{bands.price_summary.seller_ideal_price}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Max Concession</p>
                  <p className="font-semibold">{bands.price_summary.max_safe_concession}</p>
                </div>
              </div>
              <p className="text-xs text-amber-600 mt-2 bg-amber-50 p-2 rounded">
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                {bands.price_summary.escalation_threshold}
              </p>
            </CardContent>
          </Card>
          
          {/* Comparison Table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Strategy Comparison</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      {bands.comparison_table.headers.map((h, idx) => (
                        <th key={idx} className="px-3 py-2 text-left font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bands.comparison_table.rows.map((row, idx) => (
                      <tr key={idx} className="border-t">
                        {row.map((cell, cellIdx) => (
                          <td key={cellIdx} className="px-3 py-2">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          
          {/* Strategy Cards */}
          <div className="space-y-3">
            <h4 className="font-semibold flex items-center gap-2">
              <Brain className="h-4 w-4 text-purple-600" />
              Seller Strategy Options
              <Badge variant="outline" className="ml-2 text-xs">Internal Use Only</Badge>
            </h4>
            
            {bands.strategies.map((strategy, idx) => (
              <Card 
                key={idx}
                className={`cursor-pointer transition-all ${
                  strategy.name === 'Conservative' ? 'border-blue-200 hover:border-blue-400' :
                  strategy.name === 'Competitive' ? 'border-green-200 hover:border-green-400' :
                  'border-amber-200 hover:border-amber-400'
                }`}
                onClick={() => setExpandedStrategy(expandedStrategy === idx ? null : idx)}
              >
                <CardContent className="pt-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {strategy.name === 'Conservative' && <Target className="h-5 w-5 text-blue-600" />}
                      {strategy.name === 'Competitive' && <TrendingUp className="h-5 w-5 text-green-600" />}
                      {strategy.name === 'Profit-Focused' && <DollarSign className="h-5 w-5 text-amber-600" />}
                      <span className="font-semibold">{strategy.name}</span>
                      <Badge variant="secondary" className="text-xs">{strategy.approach}</Badge>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold">{strategy.price_display}</span>
                      {expandedStrategy === idx ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="grid grid-cols-4 gap-2 text-xs mb-3">
                    <div className="bg-slate-50 p-2 rounded">
                      <p className="text-muted-foreground">Margin</p>
                      <p className="font-medium">{strategy.margin_impact}</p>
                    </div>
                    <div className="bg-slate-50 p-2 rounded">
                      <p className="text-muted-foreground">Close Prob.</p>
                      <p className="font-medium">{strategy.close_probability}</p>
                    </div>
                    <div className="bg-slate-50 p-2 rounded">
                      <p className="text-muted-foreground">Concession</p>
                      <p className="font-medium">{strategy.concession_level}</p>
                    </div>
                    <div className="bg-slate-50 p-2 rounded">
                      <p className="text-muted-foreground">Risk</p>
                      <p className="font-medium text-xs">{strategy.risk_if_accepted?.slice(0, 20)}...</p>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedStrategy === idx && (
                    <div className="space-y-4 pt-3 border-t">
                      {/* Why This Price */}
                      <div className="bg-blue-50 p-3 rounded">
                        <h5 className="font-medium text-sm flex items-center gap-1 mb-1">
                          <Lightbulb className="h-4 w-4 text-blue-600" />
                          Why This Price
                        </h5>
                        <p className="text-sm">{strategy.why_this_price}</p>
                      </div>
                      
                      {/* Give-Get Logic */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-red-50 p-3 rounded">
                          <h5 className="font-medium text-sm text-red-700 mb-2">Seller Gives</h5>
                          <ul className="text-xs space-y-1">
                            {strategy.seller_gives.map((item, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <span className="text-red-500">→</span> {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-green-50 p-3 rounded">
                          <h5 className="font-medium text-sm text-green-700 mb-2">Seller Must Get</h5>
                          <ul className="text-xs space-y-1">
                            {strategy.seller_must_get.map((item, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <span className="text-green-500">✓</span> {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      {/* When to Use */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <h5 className="font-medium text-sm mb-1 text-green-700">When to Use</h5>
                          <ul className="text-xs space-y-1">
                            {strategy.when_to_use.map((item, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h5 className="font-medium text-sm mb-1 text-red-700">When NOT to Use</h5>
                          <ul className="text-xs space-y-1">
                            {strategy.when_not_to_use.map((item, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <XCircle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      {/* Conditions */}
                      <div className="bg-amber-50 p-3 rounded">
                        <h5 className="font-medium text-sm text-amber-700 mb-2 flex items-center gap-1">
                          <AlertTriangle className="h-4 w-4" />
                          Required Conditions
                        </h5>
                        <ul className="text-xs space-y-1">
                          {strategy.conditions.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Draft Opening */}
                      <div className="bg-slate-100 p-3 rounded">
                        <h5 className="font-medium text-sm mb-2">Draft Opening (Edit Before Use)</h5>
                        <p className="text-sm italic">{strategy.draft_opening}</p>
                      </div>
                      
                      {/* Action */}
                      <div className="flex gap-2 pt-2">
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStrategySelect(strategy);
                          }}
                          className="gap-1"
                          data-testid={`use-${strategy.name.toLowerCase()}-btn`}
                        >
                          <Edit3 className="h-3 w-3" />
                          Use as Starting Point
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(strategy.draft_opening);
                            toast.success('Copied to clipboard');
                          }}
                        >
                          <Clipboard className="h-3 w-3 mr-1" />
                          Copy
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Footer Notice */}
          <Alert className="bg-slate-100">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>Remember:</strong> {bands.do_not_send_notice}
            </AlertDescription>
          </Alert>
          
          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setShowInputs(true)}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Adjust Parameters
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default { AIControlledDraft, NegotiationBands };
